/**
 * JudgmentPattern Management API Client
 * Task 17.6: JudgmentPattern管理UIの構築
 * Requirements: 22.1, 22.2, 22.3, 22.4, 22.5, 22.6, 22.7, 22.8, 22.9, 22.10, 22.11
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export interface JudgmentPattern {
  id: string;
  name: string;
  description: string;
  scoreConditions: Record<string, { min: number; max: number }>;
  desc11?: string | null;
  desc12?: string | null;
  desc13?: string | null;
  desc14?: string | null;
  desc15?: string | null;
  desc16?: string | null;
  desc21?: string | null;
  desc22?: string | null;
  desc23?: string | null;
  desc24?: string | null;
  desc25?: string | null;
  desc31?: string | null;
  desc32?: string | null;
  desc33?: string | null;
  desc34?: string | null;
  desc35?: string | null;
  desc41?: string | null;
  desc42?: string | null;
  desc43?: string | null;
  desc44?: string | null;
  desc45?: string | null;
  desc46?: string | null;
  desc47?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetPatternsParams {
  name?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  limit?: number;
  offset?: number;
}

export interface GetPatternsResponse {
  patterns: JudgmentPattern[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface CreatePatternInput {
  name: string;
  description: string;
  scoreConditions: Record<string, { min: number; max: number }>;
  desc11?: string;
  desc12?: string;
  desc13?: string;
  desc14?: string;
  desc15?: string;
  desc16?: string;
  desc21?: string;
  desc22?: string;
  desc23?: string;
  desc24?: string;
  desc25?: string;
  desc31?: string;
  desc32?: string;
  desc33?: string;
  desc34?: string;
  desc35?: string;
  desc41?: string;
  desc42?: string;
  desc43?: string;
  desc44?: string;
  desc45?: string;
  desc46?: string;
  desc47?: string;
}

export interface UpdatePatternInput extends Partial<CreatePatternInput> {}

export interface PreviewPatternInput {
  scoreConditions: Record<string, { min: number; max: number }>;
  sampleScores: Record<string, number>;
}

export interface PreviewPatternResult {
  matches: boolean;
  details: Array<{
    type: string;
    required: { min: number; max: number };
    actual: number;
    matches: boolean;
  }>;
}

/**
 * Get patterns with filters and pagination
 */
export async function getPatterns(params: GetPatternsParams): Promise<GetPatternsResponse> {
  const client = getApiClient();
  const response = await client.get<GetPatternsResponse>('/admin/judgment-patterns', {
    params,
  });
  return response.data;
}

/**
 * Get pattern by ID
 */
export async function getPatternById(id: string): Promise<JudgmentPattern> {
  const client = getApiClient();
  const response = await client.get<{ pattern: JudgmentPattern }>(
    `/admin/judgment-patterns/${id}`
  );
  return response.data.pattern;
}

/**
 * Create new pattern
 */
export async function createPattern(input: CreatePatternInput): Promise<JudgmentPattern> {
  const client = getApiClient();
  const response = await client.post<{ pattern: JudgmentPattern }>(
    '/admin/judgment-patterns',
    input
  );
  return response.data.pattern;
}

/**
 * Update pattern
 */
export async function updatePattern(
  id: string,
  input: UpdatePatternInput
): Promise<JudgmentPattern> {
  const client = getApiClient();
  const response = await client.put<{ pattern: JudgmentPattern }>(
    `/admin/judgment-patterns/${id}`,
    input
  );
  return response.data.pattern;
}

/**
 * Delete pattern
 */
export async function deletePattern(id: string): Promise<void> {
  const client = getApiClient();
  await client.delete(`/admin/judgment-patterns/${id}`);
}

/**
 * Preview pattern matching
 */
export async function previewPattern(
  input: PreviewPatternInput
): Promise<PreviewPatternResult> {
  const client = getApiClient();
  const response = await client.post<{ preview: PreviewPatternResult }>(
    '/admin/judgment-patterns/preview',
    input
  );
  return response.data.preview;
}

/**
 * Export pattern as JSON
 */
export async function exportPattern(id: string): Promise<string> {
  const client = getApiClient();
  const response = await client.get<{ jsonData: string }>(
    `/admin/judgment-patterns/${id}/export`
  );
  return response.data.jsonData;
}

/**
 * Import pattern from JSON
 */
export async function importPattern(jsonData: string): Promise<JudgmentPattern> {
  const client = getApiClient();
  const response = await client.post<{ pattern: JudgmentPattern }>(
    '/admin/judgment-patterns/import',
    { jsonData }
  );
  return response.data.pattern;
}

export type BulkImportMode = 'add' | 'replace';

export interface BulkImportResult {
  success: boolean;
  totalCount: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    index: number;
    name: string;
    error: string;
  }>;
  deletedCount?: number;
}

/**
 * Bulk import patterns
 */
export async function bulkImportPatterns(
  patterns: CreatePatternInput[],
  mode: BulkImportMode
): Promise<BulkImportResult> {
  const client = getApiClient();
  const response = await client.post<{ result: BulkImportResult }>(
    '/admin/judgment-patterns/bulk-import',
    { patterns, mode }
  );
  return response.data.result;
}
